import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import {
  Target,
  Plus,
  CheckCircle,
  XCircle,
  Edit,
  Eye,
  RefreshCcw,
  TrendingUp,
  Users,
  DollarSign,
  Calendar,
  Clock,
  Award,
  AlertTriangle,
} from 'lucide-react';
import { toast } from 'react-hot-toast';

import { BASE_URL as API_URL } from '../utils/apiUrl';

// Status badge component - moved outside main component
const StatusBadge = ({ status }) => {
  const statusConfig = {
    draft: { color: 'bg-slate-600', label: 'Draft' },
    pending_approval: { color: 'bg-amber-600', label: 'Pending Approval' },
    approved: { color: 'bg-emerald-600', label: 'Approved' },
    rejected: { color: 'bg-red-600', label: 'Rejected' },
    revised: { color: 'bg-blue-600', label: 'Revised' },
    active: { color: 'bg-emerald-600', label: 'Active' },
  };
  const config = statusConfig[status] || { color: 'bg-slate-600', label: status };
  return <Badge className={`${config.color} text-white`}>{config.label}</Badge>;
};

// Progress bar component - moved outside main component
const ProgressBar = ({ value, targetValue, label, icon: Icon }) => {
  const percent = targetValue > 0 ? Math.min((value / targetValue) * 100, 100) : 0;
  const isAhead = percent >= 100;
  const isOnTrack = percent >= 75;
  const progressColor = isAhead
    ? 'bg-emerald-500'
    : isOnTrack
    ? 'bg-[#C5A059]'
    : 'bg-amber-500';

  return (
    <div className="space-y-2">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-slate-400" />
          <span className="text-sm text-slate-300">{label}</span>
        </div>
        <span className="text-sm font-medium text-white">{percent.toFixed(1)}%</span>
      </div>
      <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
        <div
          className={`h-full ${progressColor} transition-all duration-500`}
          style={{ width: `${percent}%` }}
        />
      </div>
      <div className="flex justify-between text-xs text-slate-500">
        <span>Current: {typeof value === 'number' ? value.toLocaleString() : value}</span>
        <span>Target: {typeof targetValue === 'number' ? targetValue.toLocaleString() : targetValue}</span>
      </div>
    </div>
  );
};

const TargetsPage = () => {
  const { user, token, hasRole } = useAuth();
  const isManagement = hasRole(['principal', 'business_head']);
  const isDealer = user?.role === 'dealer';

  // State
  const [loading, setLoading] = useState(true);
  const [periods, setPeriods] = useState([]);
  const [targets, setTargets] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [selectedTarget, setSelectedTarget] = useState(null);
  const [dealerProgress, setDealerProgress] = useState(null);

  // Dialogs
  const [showPeriodDialog, setShowPeriodDialog] = useState(false);
  const [showTargetDialog, setShowTargetDialog] = useState(false);
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [showReviseDialog, setShowReviseDialog] = useState(false);

  // Form states
  const [newPeriod, setNewPeriod] = useState({
    period_code: '',
    period_type: 'monthly',
    start_date: '',
    end_date: '',
    growth_phase: 'moderate',
    target_type: 'percentage',
    target_value: '',
    principal_growth_rate: '',
  });
  const [approvalComment, setApprovalComment] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [revisionData, setRevisionData] = useState({
    brokerage_monthly_target: '',
    aum_growth_target: '',
    active_clients_target: '',
    new_clients_target: '',
    reason: '',
  });
  
  // RM Targets State
  const [rmTargets, setRmTargets] = useState([]);
  const [selectedRmTarget, setSelectedRmTarget] = useState(null);
  const [showRmEditDialog, setShowRmEditDialog] = useState(false);
  const [rmEditData, setRmEditData] = useState({
    target_value: '',
    target_type: 'percentage',
    growth_phase: ''
  });

  // Fetch functions
  const fetchPeriods = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/targets/periods`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setPeriods(data);
      }
    } catch (err) {
      console.error('Error fetching periods:', err);
    }
  }, [token]);

  const fetchTargets = useCallback(async (periodId = null) => {
    try {
      let url = `${API_URL}/api/targets/dealers`;
      if (periodId) url += `?period_id=${periodId}`;
      
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setTargets(data);
      }
    } catch (err) {
      console.error('Error fetching targets:', err);
    }
  }, [token]);

  const fetchDealerProgress = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/targets/my-progress`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setDealerProgress(data);
      }
    } catch (err) {
      console.error('Error fetching dealer progress:', err);
    }
  }, [token]);

  const fetchRmTargets = useCallback(async (periodId = null) => {
    try {
      let url = `${API_URL}/api/targets/rm`;
      if (periodId) url += `?period_id=${periodId}`;
      
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setRmTargets(data);
      }
    } catch (err) {
      console.error('Error fetching RM targets:', err);
    }
  }, [token]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      if (isManagement) {
        await fetchPeriods();
        await fetchTargets();
        await fetchRmTargets();
      } else if (isDealer) {
        await fetchDealerProgress();
      }
      setLoading(false);
    };
    loadData();
  }, [isManagement, isDealer, fetchPeriods, fetchTargets, fetchDealerProgress, fetchRmTargets]);

  // Actions
  
  // Helper to auto-calculate end date based on period type
  const calculateEndDate = (startDate, periodType) => {
    if (!startDate) return '';
    const start = new Date(startDate);
    let end;
    switch (periodType) {
      case 'daily':
        end = new Date(start);
        break;
      case 'weekly':
        end = new Date(start);
        end.setDate(end.getDate() + 6);
        break;
      case 'monthly':
        end = new Date(start);
        end.setMonth(end.getMonth() + 1);
        end.setDate(end.getDate() - 1);
        break;
      case 'quarterly':
        end = new Date(start);
        end.setMonth(end.getMonth() + 3);
        end.setDate(end.getDate() - 1);
        break;
      case 'yearly':
        end = new Date(start);
        end.setFullYear(end.getFullYear() + 1);
        end.setDate(end.getDate() - 1);
        break;
      default:
        end = start;
    }
    return end.toISOString().split('T')[0];
  };
  
  // Update end date when start date or period type changes
  const handlePeriodTypeChange = (type) => {
    const endDate = calculateEndDate(newPeriod.start_date, type);
    setNewPeriod({ ...newPeriod, period_type: type, end_date: endDate });
  };
  
  const handleStartDateChange = (date) => {
    const endDate = calculateEndDate(date, newPeriod.period_type);
    setNewPeriod({ ...newPeriod, start_date: date, end_date: endDate });
  };
  
  const handleCreatePeriod = async () => {
    try {
      const payload = {
        ...newPeriod,
        target_value: newPeriod.target_value ? parseFloat(newPeriod.target_value) : null,
        principal_growth_rate: newPeriod.principal_growth_rate ? parseFloat(newPeriod.principal_growth_rate) : null,
      };
      
      const res = await fetch(`${API_URL}/api/targets/periods`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        toast.success('Target period created');
        setShowPeriodDialog(false);
        setNewPeriod({
          period_code: '',
          period_type: 'monthly',
          start_date: '',
          end_date: '',
          growth_phase: 'moderate',
          target_type: 'percentage',
          target_value: '',
          principal_growth_rate: '',
        });
        fetchPeriods();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to create period');
      }
    } catch (err) {
      toast.error('Error creating period');
    }
  };

  const handleGenerateTargets = async (periodId) => {
    try {
      const res = await fetch(`${API_URL}/api/targets/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ period_id: periodId }),
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Generated ${data.generated} targets`);
        fetchTargets(periodId);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to generate targets');
      }
    } catch (err) {
      toast.error('Error generating targets');
    }
  };

  const handleApproveTarget = async () => {
    try {
      const res = await fetch(`${API_URL}/api/targets/dealers/${selectedTarget.id}/approve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ comments: approvalComment }),
      });
      if (res.ok) {
        toast.success('Target approved');
        setShowApproveDialog(false);
        setApprovalComment('');
        fetchTargets(selectedPeriod);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to approve target');
      }
    } catch (err) {
      toast.error('Error approving target');
    }
  };

  const handleRejectTarget = async () => {
    if (!rejectionReason.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/targets/dealers/${selectedTarget.id}/reject`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ reason: rejectionReason }),
      });
      if (res.ok) {
        toast.success('Target rejected');
        setShowRejectDialog(false);
        setRejectionReason('');
        fetchTargets(selectedPeriod);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to reject target');
      }
    } catch (err) {
      toast.error('Error rejecting target');
    }
  };

  const handleReviseTarget = async () => {
    if (!revisionData.reason.trim()) {
      toast.error('Please provide a reason for revision');
      return;
    }
    const payload = { reason: revisionData.reason };
    if (revisionData.brokerage_monthly_target) {
      payload.brokerage_monthly_target = parseFloat(revisionData.brokerage_monthly_target);
    }
    if (revisionData.aum_growth_target) {
      payload.aum_growth_target = parseFloat(revisionData.aum_growth_target);
    }
    if (revisionData.active_clients_target) {
      payload.active_clients_target = parseInt(revisionData.active_clients_target);
    }
    if (revisionData.new_clients_target) {
      payload.new_clients_target = parseInt(revisionData.new_clients_target);
    }

    try {
      const res = await fetch(`${API_URL}/api/targets/dealers/${selectedTarget.id}/revise`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        toast.success('Target revised');
        setShowReviseDialog(false);
        setRevisionData({
          brokerage_monthly_target: '',
          aum_growth_target: '',
          active_clients_target: '',
          new_clients_target: '',
          reason: '',
        });
        fetchTargets(selectedPeriod);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to revise target');
      }
    } catch (err) {
      toast.error('Error revising target');
    }
  };

  // RM Target Handlers
  const handleUpdateRmTarget = async () => {
    if (!selectedRmTarget) return;
    
    try {
      const payload = {};
      if (rmEditData.target_value) {
        payload.target_value = parseFloat(rmEditData.target_value);
      }
      if (rmEditData.target_type) {
        payload.target_type = rmEditData.target_type;
      }
      if (rmEditData.growth_phase && rmEditData.growth_phase !== 'default') {
        payload.growth_phase = rmEditData.growth_phase;
      }
      
      const res = await fetch(`${API_URL}/api/targets/rm/${selectedRmTarget.rm_id}?period_id=${selectedPeriod || ''}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        toast.success('RM Target updated');
        setShowRmEditDialog(false);
        fetchRmTargets(selectedPeriod);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to update RM target');
      }
    } catch (err) {
      toast.error('Error updating RM target');
    }
  };
  
  const handleRecomputeRmAchievements = async () => {
    try {
      const res = await fetch(`${API_URL}/api/targets/rm/recompute${selectedPeriod ? `?period_id=${selectedPeriod}` : ''}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Recomputed ${data.updated} RM achievements`);
        fetchRmTargets(selectedPeriod);
      } else {
        toast.error('Failed to recompute');
      }
    } catch (err) {
      toast.error('Error recomputing');
    }
  };

  // Render Dealer Progress View
  const renderDealerProgressView = () => {
    if (!dealerProgress || !dealerProgress.target) {
      return (
        <Card className="card-dark border-white/5">
          <CardContent className="py-10 text-center">
            <Target className="w-12 h-12 mx-auto text-slate-500 mb-4" />
            <p className="text-slate-400">No approved targets found for you.</p>
            <p className="text-sm text-slate-500 mt-2">
              Contact your Business Head for target assignments.
            </p>
          </CardContent>
        </Card>
      );
    }

    const { target, progress, period_code } = dealerProgress;

    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-xl font-semibold text-white">My Targets</h2>
            <p className="text-sm text-slate-400">Period: {period_code}</p>
          </div>
          <StatusBadge status={target.status} />
        </div>

        {/* Progress Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="card-dark border-white/5">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-slate-400 flex items-center gap-2">
                <DollarSign className="w-4 h-4" /> Brokerage
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ProgressBar
                value={progress.brokerage.current}
                targetValue={progress.brokerage.target}
                label="Monthly Target"
                icon={TrendingUp}
              />
            </CardContent>
          </Card>

          <Card className="card-dark border-white/5">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-slate-400 flex items-center gap-2">
                <TrendingUp className="w-4 h-4" /> AUM
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ProgressBar
                value={progress.aum.current}
                targetValue={progress.aum.target}
                label="Closing AUM Target"
                icon={TrendingUp}
              />
            </CardContent>
          </Card>

          <Card className="card-dark border-white/5">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-slate-400 flex items-center gap-2">
                <Users className="w-4 h-4" /> Active Clients
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ProgressBar
                value={progress.active_clients.current}
                targetValue={progress.active_clients.target}
                label="Active Clients"
                icon={Users}
              />
            </CardContent>
          </Card>
        </div>

        {/* Detailed Target Breakdown */}
        <Card className="card-dark border-white/5">
          <CardHeader>
            <CardTitle className="text-white">Target Breakdown</CardTitle>
            <CardDescription>Detailed view of your targets for {period_code}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Brokerage Targets */}
              <div className="space-y-4">
                <h4 className="font-medium text-white flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-[#C5A059]" /> Brokerage Targets
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-400">Monthly Target</span>
                    <span className="text-white">₹{target.brokerage_targets?.monthly_target?.toLocaleString() || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Quarterly Target</span>
                    <span className="text-white">₹{target.brokerage_targets?.quarterly_target?.toLocaleString() || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Equity Target</span>
                    <span className="text-white">₹{target.brokerage_targets?.equity_target?.toLocaleString() || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Derivatives Target</span>
                    <span className="text-white">₹{target.brokerage_targets?.derivatives_target?.toLocaleString() || 0}</span>
                  </div>
                </div>
              </div>

              {/* AUM Targets */}
              <div className="space-y-4">
                <h4 className="font-medium text-white flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-emerald-500" /> AUM Targets
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-400">Closing AUM Target</span>
                    <span className="text-white">₹{target.aum_targets?.closing_aum_target?.toLocaleString() || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">AUM Growth Target</span>
                    <span className="text-white">₹{target.aum_targets?.aum_growth_target?.toLocaleString() || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Growth %</span>
                    <span className="text-white">{target.aum_targets?.aum_growth_percent || 0}%</span>
                  </div>
                </div>
              </div>

              {/* Client Targets */}
              <div className="space-y-4">
                <h4 className="font-medium text-white flex items-center gap-2">
                  <Users className="w-4 h-4 text-sky-500" /> Client Targets
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-400">Active Clients Target</span>
                    <span className="text-white">{target.client_targets?.active_clients_target || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">New Clients Target</span>
                    <span className="text-white">{target.client_targets?.new_clients_target || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Client Activation Target</span>
                    <span className="text-white">{target.client_targets?.client_activation_target || 0}</span>
                  </div>
                </div>
              </div>

              {/* Retention & Compliance */}
              <div className="space-y-4">
                <h4 className="font-medium text-white flex items-center gap-2">
                  <Award className="w-4 h-4 text-violet-500" /> Retention & Compliance
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-400">Client Retention Rate</span>
                    <span className="text-white">{target.retention_targets?.client_retention_rate || 0}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">AUM Retention Rate</span>
                    <span className="text-white">{target.retention_targets?.aum_retention_rate || 0}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Compliance Score Target</span>
                    <span className="text-white">{target.compliance_targets?.target_score || 0}/100</span>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  // Render Management View
  const renderManagementView = () => (
    <Tabs defaultValue="periods" className="space-y-6">
      <TabsList className="bg-slate-800">
        <TabsTrigger value="periods" className="data-[state=active]:bg-[#C5A059]">
          Target Periods
        </TabsTrigger>
        <TabsTrigger value="rm" className="data-[state=active]:bg-[#C5A059]">
          RM Targets
        </TabsTrigger>
        <TabsTrigger value="dealers" className="data-[state=active]:bg-[#C5A059]">
          Dealer Targets
        </TabsTrigger>
      </TabsList>

      {/* Target Periods Tab */}
      <TabsContent value="periods" className="space-y-4">
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-semibold text-white">Target Periods</h2>
          {user?.role === 'principal' && (
            <Button onClick={() => setShowPeriodDialog(true)} className="bg-[#C5A059] hover:bg-[#b4914e]">
              <Plus className="w-4 h-4 mr-2" /> Create Period
            </Button>
          )}
        </div>

        {periods.length === 0 ? (
          <Card className="card-dark border-white/5">
            <CardContent className="py-10 text-center">
              <Calendar className="w-12 h-12 mx-auto text-slate-500 mb-4" />
              <p className="text-slate-400">No target periods created yet.</p>
              <p className="text-sm text-slate-500 mt-2">
                Create a period to start generating dealer targets.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {periods.map((period) => (
              <Card key={period.id} className="card-dark border-white/5 hover:border-[#C5A059]/30 transition-colors">
                <CardHeader className="pb-2">
                  <div className="flex justify-between items-start">
                    <CardTitle className="text-white">{period.period_code}</CardTitle>
                    <StatusBadge status={period.status} />
                  </div>
                  <CardDescription className="capitalize">{period.period_type}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="text-sm space-y-1">
                    <div className="flex justify-between">
                      <span className="text-slate-400">Start Date</span>
                      <span className="text-white">{period.start_date}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">End Date</span>
                      <span className="text-white">{period.end_date}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Growth Phase</span>
                      <Badge variant="outline" className="capitalize text-[#C5A059] border-[#C5A059]">
                        {period.config?.growth_phase || 'moderate'}
                      </Badge>
                    </div>
                    {period.config?.target_type && (
                      <div className="flex justify-between">
                        <span className="text-slate-400">Target Type</span>
                        <span className="text-white capitalize">{period.config.target_type}</span>
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="flex-1"
                      onClick={() => {
                        setSelectedPeriod(period.id);
                        fetchTargets(period.id);
                        fetchRmTargets(period.id);
                      }}
                    >
                      <Eye className="w-4 h-4 mr-1" /> View Targets
                    </Button>
                    {period.status === 'draft' && (
                      <Button
                        size="sm"
                        className="bg-[#C5A059] hover:bg-[#b4914e]"
                        onClick={() => handleGenerateTargets(period.id)}
                      >
                        <RefreshCcw className="w-4 h-4 mr-1" /> Generate
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </TabsContent>

      {/* RM Targets Tab */}
      <TabsContent value="rm" className="space-y-4">
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-semibold text-white">RM Targets</h2>
          <div className="flex items-center gap-2">
            <Select value={selectedPeriod || 'all'} onValueChange={(v) => {
              const periodId = v === 'all' ? null : v;
              setSelectedPeriod(periodId);
              fetchRmTargets(periodId);
            }}>
              <SelectTrigger className="w-48 bg-slate-800 border-white/10">
                <SelectValue placeholder="All Periods" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Periods</SelectItem>
                {periods.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.period_code}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button 
              variant="outline" 
              size="sm"
              onClick={handleRecomputeRmAchievements}
              className="border-[#C5A059]/30"
            >
              <RefreshCcw className="w-4 h-4 mr-1" /> Recompute
            </Button>
          </div>
        </div>

        {rmTargets.length === 0 ? (
          <Card className="card-dark border-white/5">
            <CardContent className="py-10 text-center">
              <Users className="w-12 h-12 mx-auto text-slate-500 mb-4" />
              <p className="text-slate-400">No RM targets found.</p>
              <p className="text-sm text-slate-500 mt-2">
                RM targets are aggregated from dealer targets.
              </p>
            </CardContent>
          </Card>
        ) : (
          <Card className="card-dark border-white/5">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">RM Code</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">RM Name</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Target Value</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Achieved</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Achievement %</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Growth Phase</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Period</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Status</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-slate-400 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {rmTargets.map((rm) => (
                      <tr key={rm.rm_id} className="hover:bg-white/5">
                        <td className="px-4 py-3 text-sm text-white">{rm.rm_code || '-'}</td>
                        <td className="px-4 py-3 text-sm text-white">{rm.rm_name}</td>
                        <td className="px-4 py-3 text-sm text-white">
                          {rm.target_type === 'percentage' 
                            ? `${rm.target_value || 0}%` 
                            : `₹${(rm.target_value || 0).toLocaleString()}`}
                        </td>
                        <td className="px-4 py-3 text-sm text-white">
                          <div>₹{rm.achieved_brokerage?.toLocaleString() || 0}</div>
                          <div className="text-xs text-slate-400">AUM: ₹{rm.achieved_aum?.toLocaleString() || 0}</div>
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <span className={`font-medium ${
                            rm.achievement_percent >= 100 ? 'text-emerald-400' : 
                            rm.achievement_percent >= 75 ? 'text-amber-400' : 'text-red-400'
                          }`}>
                            {rm.achievement_percent?.toFixed(1) || 0}%
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <Badge variant="outline" className="capitalize text-[#C5A059] border-[#C5A059]">
                            {rm.growth_phase || 'inherited'}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-400">{rm.period_code || '-'}</td>
                        <td className="px-4 py-3 text-sm">
                          <Badge className={`${
                            rm.status === 'On Track' ? 'bg-emerald-600' : 
                            rm.status === 'Lagging' ? 'bg-amber-600' : 'bg-red-600'
                          } text-white`}>
                            {rm.status}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                setSelectedRmTarget(rm);
                                setRmEditData({
                                  target_value: rm.target_value || '',
                                  target_type: rm.target_type || 'percentage',
                                  growth_phase: rm.growth_phase || ''
                                });
                                setShowRmEditDialog(true);
                              }}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
      </TabsContent>

      {/* Dealer Targets Tab */}
      <TabsContent value="dealers" className="space-y-4">
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-semibold text-white">Dealer Targets</h2>
          <div className="flex items-center gap-2">
            <Select value={selectedPeriod || 'all'} onValueChange={(v) => {
              const periodId = v === 'all' ? null : v;
              setSelectedPeriod(periodId);
              fetchTargets(periodId);
            }}>
              <SelectTrigger className="w-48 bg-slate-800 border-white/10">
                <SelectValue placeholder="All Periods" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Periods</SelectItem>
                {periods.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.period_code}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {targets.length === 0 ? (
          <Card className="card-dark border-white/5">
            <CardContent className="py-10 text-center">
              <Target className="w-12 h-12 mx-auto text-slate-500 mb-4" />
              <p className="text-slate-400">No targets found.</p>
              <p className="text-sm text-slate-500 mt-2">
                Generate targets from a period to see them here.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {targets.map((target) => (
              <Card key={target.id} className="card-dark border-white/5">
                <CardContent className="p-4">
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    {/* Dealer Info */}
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-[#C5A059]/20 flex items-center justify-center">
                        <Users className="w-5 h-5 text-[#C5A059]" />
                      </div>
                      <div>
                        <p className="font-medium text-white">{target.dealer_name}</p>
                        <p className="text-sm text-slate-400">{target.period_code}</p>
                      </div>
                    </div>

                    {/* Target Summary */}
                    <div className="flex flex-wrap gap-4 lg:gap-6">
                      <div className="text-center">
                        <p className="text-xs text-slate-400">Brokerage</p>
                        <p className="font-medium text-white">
                          ₹{target.brokerage_targets?.monthly_target?.toLocaleString() || 0}
                        </p>
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-slate-400">AUM Target</p>
                        <p className="font-medium text-white">
                          ₹{target.aum_targets?.closing_aum_target?.toLocaleString() || 0}
                        </p>
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-slate-400">Clients</p>
                        <p className="font-medium text-white">
                          {target.client_targets?.active_clients_target || 0}
                        </p>
                      </div>
                    </div>

                    {/* Status & Actions */}
                    <div className="flex items-center gap-3">
                      <StatusBadge status={target.status} />
                      
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setSelectedTarget(target);
                            setShowTargetDialog(true);
                          }}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        
                        {target.status === 'pending_approval' && (
                          <>
                            <Button
                              size="sm"
                              className="bg-emerald-600 hover:bg-emerald-700"
                              onClick={() => {
                                setSelectedTarget(target);
                                setShowApproveDialog(true);
                              }}
                            >
                              <CheckCircle className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => {
                                setSelectedTarget(target);
                                setShowRejectDialog(true);
                              }}
                            >
                              <XCircle className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                        
                        {['pending_approval', 'approved'].includes(target.status) && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setSelectedTarget(target);
                              setRevisionData({
                                brokerage_monthly_target: target.brokerage_targets?.monthly_target || '',
                                aum_growth_target: target.aum_targets?.aum_growth_target || '',
                                active_clients_target: target.client_targets?.active_clients_target || '',
                                new_clients_target: target.client_targets?.new_clients_target || '',
                                reason: '',
                              });
                              setShowReviseDialog(true);
                            }}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </TabsContent>
    </Tabs>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#C5A059]" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white flex items-center gap-2">
          <Target className="w-7 h-7 text-[#C5A059]" />
          Target Management
        </h1>
        <p className="text-slate-400 mt-1">
          {isManagement
            ? 'Manage dealer targets and approval workflows'
            : 'View your targets and track progress'}
        </p>
      </div>

      {/* AI Notice */}
      <Card className="card-dark border-amber-500/20 bg-amber-500/5">
        <CardContent className="py-3 flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0" />
          <p className="text-sm text-amber-200">
            <strong>Rule-Based Targets:</strong> All targets are generated using historical performance data and configurable growth factors. AI-powered recommendations are not active.
          </p>
        </CardContent>
      </Card>

      {/* Main Content */}
      {isDealer ? renderDealerProgressView() : renderManagementView()}

      {/* Create Period Dialog */}
      <Dialog open={showPeriodDialog} onOpenChange={setShowPeriodDialog}>
        <DialogContent className="bg-slate-900 border-white/10 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white">Create Target Period</DialogTitle>
            <DialogDescription>Define a new target period for your organization.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
            <div>
              <Label>Period Code</Label>
              <Input
                placeholder="e.g., 2025-Q1 or 2025-01"
                value={newPeriod.period_code}
                onChange={(e) => setNewPeriod({ ...newPeriod, period_code: e.target.value })}
                className="bg-slate-800 border-white/10"
              />
            </div>
            <div>
              <Label>Period Type</Label>
              <Select
                value={newPeriod.period_type}
                onValueChange={handlePeriodTypeChange}
              >
                <SelectTrigger className="bg-slate-800 border-white/10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Daily (1 day)</SelectItem>
                  <SelectItem value="weekly">Weekly (7 days)</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="quarterly">Quarterly (3 months)</SelectItem>
                  <SelectItem value="yearly">Yearly (12 months)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={newPeriod.start_date}
                  onChange={(e) => handleStartDateChange(e.target.value)}
                  className="bg-slate-800 border-white/10"
                />
              </div>
              <div>
                <Label>End Date</Label>
                <Input
                  type="date"
                  value={newPeriod.end_date}
                  onChange={(e) => setNewPeriod({ ...newPeriod, end_date: e.target.value })}
                  className="bg-slate-800 border-white/10"
                />
                <p className="text-xs text-slate-500 mt-1">Auto-calculated based on period type</p>
              </div>
            </div>
            <div>
              <Label>Growth Phase</Label>
              <Select
                value={newPeriod.growth_phase}
                onValueChange={(v) => setNewPeriod({ ...newPeriod, growth_phase: v })}
              >
                <SelectTrigger className="bg-slate-800 border-white/10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="principal">Principal Defined (Manual %)</SelectItem>
                  <SelectItem value="aggressive">Aggressive (15% growth)</SelectItem>
                  <SelectItem value="moderate">Moderate (10% growth)</SelectItem>
                  <SelectItem value="stable">Stable (5% growth)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {newPeriod.growth_phase === 'principal' && (
              <div>
                <Label>Principal Growth Rate (%)</Label>
                <Input
                  type="number"
                  placeholder="e.g., 12"
                  value={newPeriod.principal_growth_rate}
                  onChange={(e) => setNewPeriod({ ...newPeriod, principal_growth_rate: e.target.value })}
                  className="bg-slate-800 border-white/10"
                />
                <p className="text-xs text-slate-500 mt-1">This % will override default growth calculations for all targets</p>
              </div>
            )}
            
            <div>
              <Label>Target Type</Label>
              <Select
                value={newPeriod.target_type}
                onValueChange={(v) => setNewPeriod({ ...newPeriod, target_type: v })}
              >
                <SelectTrigger className="bg-slate-800 border-white/10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="percentage">Percentage (% growth from previous)</SelectItem>
                  <SelectItem value="numeric">Numeric (absolute value)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">
                {newPeriod.target_type === 'percentage' 
                  ? 'Targets will be calculated as growth % over last period'
                  : 'Targets will be fixed numeric values'}
              </p>
            </div>
            
            {newPeriod.target_type === 'numeric' && (
              <div>
                <Label>Base Target Value (₹)</Label>
                <Input
                  type="number"
                  placeholder="e.g., 1000000"
                  value={newPeriod.target_value}
                  onChange={(e) => setNewPeriod({ ...newPeriod, target_value: e.target.value })}
                  className="bg-slate-800 border-white/10"
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowPeriodDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreatePeriod} className="bg-[#C5A059] hover:bg-[#b4914e]">
              Create Period
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Target Dialog */}
      <Dialog open={showTargetDialog} onOpenChange={setShowTargetDialog}>
        <DialogContent className="bg-slate-900 border-white/10 max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">Target Details</DialogTitle>
            <DialogDescription>
              {selectedTarget?.dealer_name} - {selectedTarget?.period_code}
            </DialogDescription>
          </DialogHeader>
          {selectedTarget && (
            <div className="space-y-6">
              {/* Status */}
              <div className="flex items-center gap-3">
                <StatusBadge status={selectedTarget.status} />
                <span className="text-sm text-slate-400">Version: {selectedTarget.version}</span>
              </div>

              {/* Brokerage */}
              <div>
                <h4 className="font-medium text-white mb-2">Brokerage Targets</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="flex justify-between bg-slate-800 p-2 rounded">
                    <span className="text-slate-400">Monthly</span>
                    <span className="text-white">₹{selectedTarget.brokerage_targets?.monthly_target?.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between bg-slate-800 p-2 rounded">
                    <span className="text-slate-400">Quarterly</span>
                    <span className="text-white">₹{selectedTarget.brokerage_targets?.quarterly_target?.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between bg-slate-800 p-2 rounded">
                    <span className="text-slate-400">Baseline</span>
                    <span className="text-white">₹{selectedTarget.brokerage_targets?.baseline?.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between bg-slate-800 p-2 rounded">
                    <span className="text-slate-400">Stretch</span>
                    <span className="text-white">₹{selectedTarget.brokerage_targets?.stretch_target?.toLocaleString()}</span>
                  </div>
                </div>
              </div>

              {/* AUM */}
              <div>
                <h4 className="font-medium text-white mb-2">AUM Targets</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="flex justify-between bg-slate-800 p-2 rounded">
                    <span className="text-slate-400">Closing AUM</span>
                    <span className="text-white">₹{selectedTarget.aum_targets?.closing_aum_target?.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between bg-slate-800 p-2 rounded">
                    <span className="text-slate-400">Growth Target</span>
                    <span className="text-white">₹{selectedTarget.aum_targets?.aum_growth_target?.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between bg-slate-800 p-2 rounded">
                    <span className="text-slate-400">Growth %</span>
                    <span className="text-white">{selectedTarget.aum_targets?.aum_growth_percent}%</span>
                  </div>
                  <div className="flex justify-between bg-slate-800 p-2 rounded">
                    <span className="text-slate-400">Baseline</span>
                    <span className="text-white">₹{selectedTarget.aum_targets?.baseline_aum?.toLocaleString()}</span>
                  </div>
                </div>
              </div>

              {/* Clients */}
              <div>
                <h4 className="font-medium text-white mb-2">Client Targets</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="flex justify-between bg-slate-800 p-2 rounded">
                    <span className="text-slate-400">Active Clients</span>
                    <span className="text-white">{selectedTarget.client_targets?.active_clients_target}</span>
                  </div>
                  <div className="flex justify-between bg-slate-800 p-2 rounded">
                    <span className="text-slate-400">New Clients</span>
                    <span className="text-white">{selectedTarget.client_targets?.new_clients_target}</span>
                  </div>
                </div>
              </div>

              {/* Adjustments */}
              <div>
                <h4 className="font-medium text-white mb-2">Applied Adjustments</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="flex justify-between bg-slate-800 p-2 rounded">
                    <span className="text-slate-400">Base Growth Rate</span>
                    <span className="text-white">{(selectedTarget.adjustments?.base_growth_rate * 100).toFixed(0)}%</span>
                  </div>
                  <div className="flex justify-between bg-slate-800 p-2 rounded">
                    <span className="text-slate-400">Tenure Factor</span>
                    <span className="text-white">{selectedTarget.adjustments?.dealer_tenure_factor}</span>
                  </div>
                  <div className="flex justify-between bg-slate-800 p-2 rounded">
                    <span className="text-slate-400">Seasonal Factor</span>
                    <span className="text-white">{selectedTarget.adjustments?.seasonal_factor}</span>
                  </div>
                  <div className="flex justify-between bg-slate-800 p-2 rounded">
                    <span className="text-slate-400">Capacity Factor</span>
                    <span className="text-white">{selectedTarget.adjustments?.capacity_factor}</span>
                  </div>
                </div>
              </div>

              {/* Workflow */}
              {selectedTarget.workflow && (
                <div>
                  <h4 className="font-medium text-white mb-2">Workflow History</h4>
                  <div className="space-y-2 text-sm">
                    {selectedTarget.workflow.generated_at && (
                      <div className="flex items-center gap-2 text-slate-400">
                        <Clock className="w-4 h-4" />
                        Generated: {new Date(selectedTarget.workflow.generated_at).toLocaleString()}
                      </div>
                    )}
                    {selectedTarget.workflow.approved_at && (
                      <div className="flex items-center gap-2 text-emerald-400">
                        <CheckCircle className="w-4 h-4" />
                        Approved: {new Date(selectedTarget.workflow.approved_at).toLocaleString()}
                        {selectedTarget.workflow.review_comments && (
                          <span className="text-slate-400">- &quot;{selectedTarget.workflow.review_comments}&quot;</span>
                        )}
                      </div>
                    )}
                    {selectedTarget.workflow.rejection_reason && (
                      <div className="flex items-center gap-2 text-red-400">
                        <XCircle className="w-4 h-4" />
                        Rejected: {selectedTarget.workflow.rejection_reason}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowTargetDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approve Dialog */}
      <Dialog open={showApproveDialog} onOpenChange={setShowApproveDialog}>
        <DialogContent className="bg-slate-900 border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white">Approve Target</DialogTitle>
            <DialogDescription>
              Approve target for {selectedTarget?.dealer_name}
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label>Comments (Optional)</Label>
            <Textarea
              placeholder="Add approval comments..."
              value={approvalComment}
              onChange={(e) => setApprovalComment(e.target.value)}
              className="bg-slate-800 border-white/10"
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowApproveDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleApproveTarget} className="bg-emerald-600 hover:bg-emerald-700">
              <CheckCircle className="w-4 h-4 mr-2" /> Approve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent className="bg-slate-900 border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white">Reject Target</DialogTitle>
            <DialogDescription>
              Reject target for {selectedTarget?.dealer_name}
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label>Rejection Reason *</Label>
            <Textarea
              placeholder="Provide reason for rejection..."
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              className="bg-slate-800 border-white/10"
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowRejectDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleRejectTarget} variant="destructive">
              <XCircle className="w-4 h-4 mr-2" /> Reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Revise Dialog */}
      <Dialog open={showReviseDialog} onOpenChange={setShowReviseDialog}>
        <DialogContent className="bg-slate-900 border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white">Revise Target</DialogTitle>
            <DialogDescription>
              Make manual adjustments to {selectedTarget?.dealer_name}&apos;s targets
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Monthly Brokerage Target</Label>
              <Input
                type="number"
                placeholder="Enter new target"
                value={revisionData.brokerage_monthly_target}
                onChange={(e) =>
                  setRevisionData({ ...revisionData, brokerage_monthly_target: e.target.value })
                }
                className="bg-slate-800 border-white/10"
              />
            </div>
            <div>
              <Label>AUM Growth Target</Label>
              <Input
                type="number"
                placeholder="Enter new target"
                value={revisionData.aum_growth_target}
                onChange={(e) =>
                  setRevisionData({ ...revisionData, aum_growth_target: e.target.value })
                }
                className="bg-slate-800 border-white/10"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Active Clients Target</Label>
                <Input
                  type="number"
                  placeholder="Enter target"
                  value={revisionData.active_clients_target}
                  onChange={(e) =>
                    setRevisionData({ ...revisionData, active_clients_target: e.target.value })
                  }
                  className="bg-slate-800 border-white/10"
                />
              </div>
              <div>
                <Label>New Clients Target</Label>
                <Input
                  type="number"
                  placeholder="Enter target"
                  value={revisionData.new_clients_target}
                  onChange={(e) =>
                    setRevisionData({ ...revisionData, new_clients_target: e.target.value })
                  }
                  className="bg-slate-800 border-white/10"
                />
              </div>
            </div>
            <div>
              <Label>Revision Reason *</Label>
              <Textarea
                placeholder="Explain the reason for this revision..."
                value={revisionData.reason}
                onChange={(e) => setRevisionData({ ...revisionData, reason: e.target.value })}
                className="bg-slate-800 border-white/10"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowReviseDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleReviseTarget} className="bg-[#C5A059] hover:bg-[#b4914e]">
              <Edit className="w-4 h-4 mr-2" /> Save Revision
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* RM Target Edit Dialog */}
      <Dialog open={showRmEditDialog} onOpenChange={setShowRmEditDialog}>
        <DialogContent className="bg-slate-900 border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white">Edit RM Target</DialogTitle>
            <DialogDescription>
              Adjust target for {selectedRmTarget?.rm_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Target Type</Label>
              <Select
                value={rmEditData.target_type}
                onValueChange={(v) => setRmEditData({ ...rmEditData, target_type: v })}
              >
                <SelectTrigger className="bg-slate-800 border-white/10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="percentage">Percentage (%)</SelectItem>
                  <SelectItem value="numeric">Numeric (₹)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Target Value {rmEditData.target_type === 'percentage' ? '(%)' : '(₹)'}</Label>
              <Input
                type="number"
                placeholder={rmEditData.target_type === 'percentage' ? "e.g., 10" : "e.g., 1000000"}
                value={rmEditData.target_value}
                onChange={(e) => setRmEditData({ ...rmEditData, target_value: e.target.value })}
                className="bg-slate-800 border-white/10"
              />
            </div>
            <div>
              <Label>Growth Phase (Override)</Label>
              <Select
                value={rmEditData.growth_phase}
                onValueChange={(v) => setRmEditData({ ...rmEditData, growth_phase: v })}
              >
                <SelectTrigger className="bg-slate-800 border-white/10">
                  <SelectValue placeholder="Use period default" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="default">Use Period Default</SelectItem>
                  <SelectItem value="principal">Principal Defined</SelectItem>
                  <SelectItem value="aggressive">Aggressive</SelectItem>
                  <SelectItem value="moderate">Moderate</SelectItem>
                  <SelectItem value="stable">Stable</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="p-3 bg-slate-800/50 rounded-lg">
              <p className="text-xs text-slate-400">Current Achievement</p>
              <div className="flex justify-between mt-2">
                <span className="text-sm text-white">Brokerage: ₹{selectedRmTarget?.achieved_brokerage?.toLocaleString() || 0}</span>
                <span className="text-sm text-white">AUM: ₹{selectedRmTarget?.achieved_aum?.toLocaleString() || 0}</span>
              </div>
              <div className="mt-2">
                <span className={`text-sm font-medium ${
                  (selectedRmTarget?.achievement_percent || 0) >= 100 ? 'text-emerald-400' : 
                  (selectedRmTarget?.achievement_percent || 0) >= 75 ? 'text-amber-400' : 'text-red-400'
                }`}>
                  {selectedRmTarget?.achievement_percent?.toFixed(1) || 0}% Achievement
                </span>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowRmEditDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateRmTarget} className="bg-[#C5A059] hover:bg-[#b4914e]">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TargetsPage;
